import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function NewTransactionModal({ defaultDate, editing, onClose, onCreated }) {
  const [refs, setRefs] = useState(null);
  const [profile, setProfile] = useState(null);
  const isEdit = !!editing;

  const [form, setForm] = useState(
    isEdit
      ? {
          txn_inv: editing.txn_inv || "",
          txn_amt: String(editing.txn_amt ?? ""),
          txn_chk: editing.txn_chk || "",
          txn_note: editing.txn_note || "",
          req_init: editing.req_init || "",
          pye_id: editing.pye_id || "",
          codes_id: editing.codes_id || "",
          pay_tp: String(editing.pay_tp ?? ""),
          pay_dt: editing.pay_dt || defaultDate || todayIso(),
          post_dt: editing.post_dt || defaultDate || todayIso(),
        }
      : {
          txn_inv: "N/A",
          txn_amt: "",
          txn_chk: "",
          txn_note: "",
          req_init: "",
          pye_id: "",
          codes_id: "",
          pay_tp: "0",         // new transactions are Pending until approved
          pay_dt: todayIso(),  // always default to today regardless of FY
          post_dt: todayIso(), // request date — always today when creating
        }
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let ignore = false;
    api.refs()
      .then((r) => { if (!ignore) setRefs(r); })
      .catch((e) => { if (!ignore) setError(`Failed to load: ${e.message}`); });
    return () => { ignore = true; };
  }, []);

  // Pull /api/me to default Requestor + Payee from the current user's name.
  useEffect(() => {
    if (isEdit) return undefined;
    let ignore = false;
    api.me().then((p) => { if (!ignore) setProfile(p); }).catch(() => {});
    return () => { ignore = true; };
  }, [isEdit]);

  useEffect(() => {
    if (isEdit || !profile) return;
    setForm((f) => {
      const next = { ...f };
      if (!next.req_init && profile.req_init) next.req_init = profile.req_init;
      if (!next.pye_id && profile.pye_id) next.pye_id = profile.pye_id;
      return next;
    });
  }, [profile, isEdit]);

  const valid = useMemo(() => {
    const amt = Number(form.txn_amt);
    return (
      form.txn_inv.trim() &&
      !Number.isNaN(amt) && amt > 0 &&
      form.pye_id && form.codes_id
    );
  }, [form]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const body = {
        txn_inv: form.txn_inv.trim(),
        txn_amt: Number(form.txn_amt),
        txn_chk: form.txn_chk.trim() || null,
        txn_note: form.txn_note.trim() || null,
        req_init: form.req_init,
        pye_id: form.pye_id,
        codes_id: form.codes_id,
        pay_tp: Number(form.pay_tp),
        pay_dt: form.pay_dt,
        post_dt: form.post_dt,
      };
      const r = isEdit
        ? await api.updateTransaction(editing.txn_seq, body)
        : await api.createTransaction(body);
      onCreated(r);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-label="New transaction"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-txn-modal"
      >
        <div className="modal-head">
          <h2>{isEdit ? `Edit transaction · ${editing.txn_inv}` : "New transaction"}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {!refs ? (
          <div className="loading-row" style={{ padding: "30px 0" }}>
            <span className="spinner" /> Loading reference data…
          </div>
        ) : (
          <form className="modal-form" onSubmit={submit}>
            <div className="form-grid">
              <div className="form-field">
                <label>Invoice <span className="req">*</span></label>
                <input
                  data-testid="ntx-inv"
                  value={form.txn_inv}
                  maxLength={20}
                  onChange={(e) => set("txn_inv", e.target.value)}
                  required
                />
              </div>

              <div className="form-field">
                <label>Amount (USD) <span className="req">*</span></label>
                <input
                  data-testid="ntx-amt"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={form.txn_amt}
                  onChange={(e) => set("txn_amt", e.target.value)}
                  onBlur={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isNaN(n) && e.target.value !== "")
                      set("txn_amt", n.toFixed(2));
                  }}
                  required
                />
              </div>

              <div className="form-field">
                <label>Payee <span className="req">*</span></label>
                <select
                  data-testid="ntx-pye"
                  value={form.pye_id}
                  onChange={(e) => set("pye_id", e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {refs.payees.map((p) => (
                    <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-field form-field-wide">
                <label>Code <span className="req">*</span></label>
                <select
                  data-testid="ntx-code"
                  value={form.codes_id}
                  onChange={(e) => set("codes_id", e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {refs.codes.map((c) => (
                    <option key={c.id} value={c.id}>{c.id} · {c.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-field form-field-wide">
                <label>Notes</label>
                <textarea
                  data-testid="ntx-note"
                  className="notes-textarea"
                  value={form.txn_note}
                  maxLength={50}
                  rows={4}
                  placeholder="Optional — up to 50 characters"
                  onChange={(e) => set("txn_note", e.target.value)}
                />
              </div>

              {isEdit && (
                <>
                  <div className="form-field">
                    <label>Check #</label>
                    <input
                      data-testid="ntx-chk"
                      value={form.txn_chk}
                      maxLength={5}
                      onChange={(e) => set("txn_chk", e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Payment type</label>
                    <select
                      data-testid="ntx-pay"
                      value={form.pay_tp}
                      onChange={(e) => set("pay_tp", e.target.value)}
                    >
                      {refs.payments.map((p) => (
                        <option key={p.id} value={p.id}>{p.id} · {p.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label>Pay date</label>
                    <input
                      data-testid="ntx-paydt"
                      type="date"
                      value={form.pay_dt}
                      onChange={(e) => set("pay_dt", e.target.value)}
                    />
                  </div>

                  <div className="form-field">
                    <label>Post date</label>
                    <input
                      data-testid="ntx-postdt"
                      type="date"
                      value={form.post_dt}
                      onChange={(e) => set("post_dt", e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            {error && <div className="form-error" data-testid="ntx-error">{error}</div>}

            <div className="modal-actions">
              <button
                type="submit"
                className="btn-primary"
                data-testid="ntx-submit"
                disabled={!valid || submitting}
                style={{ width: "auto", padding: "10px 22px", margin: 0 }}
              >
                {(() => {
                  if (submitting) return "Saving…";
                  if (isEdit) return "Save changes";
                  return "Create transaction";
                })()}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
